import React, { useEffect } from 'react';
import { Container } from 'react-bootstrap';
import './LegalPages.css';

function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <Container className="legal-page">
      <div className="legal-content">
        <div className="legal-header">
          <h1>Terms of Service for ZDIVE</h1>
          <p className="legal-date">Last Updated: August, 2026</p>
        </div>

        <section className="legal-section">
          <h2>Agreement to Terms</h2>
          <p>
            By downloading, installing, or using the ZDIVE mobile application ("App") and related services (collectively, the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use our Service.
          </p>
        </section>

        <section className="legal-section">
          <h2>Description of Service</h2>
          <p>ZDIVE is a comprehensive diving application designed for iOS and watchOS that provides:</p>
          <ul>
            <li>Real-time dive tracking and monitoring</li>
            <li>Decompression calculations using industry-standard algorithms</li>
            <li>Dive log management and synchronization</li>
            <li>Equipment and trip planning tools</li>
            <li>Health and fitness integration</li>
            <li>Safety alerts and recommendations</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>Eligibility</h2>
          <p>
            You must be at least 13 years old to use this Service. If you are under 18, you must have parental or guardian consent to use the Service. By using the Service, you represent and warrant that you meet these age requirements and have the legal capacity to enter into this agreement.
          </p>
        </section>

        <section className="legal-section">
          <h2>Health and Safety Disclaimer</h2>

          <div className="legal-subsection safety-warning">
            <h3>Dive Instrument Scope</h3>
            <ul>
              <li><strong className="critical-warning">ZDIVE is a dive planning, logging, and monitoring instrument. It is not a replacement for a certified primary dive computer, proper training, or buddy procedures</strong></li>
              <li>Always carry a certified primary dive computer and follow its more conservative reading whenever the two disagree</li>
              <li>ZDIVE's decompression calculations use the industry-standard Bühlmann ZH-L16C algorithm with gradient factors and are validated against published reference tables on every release</li>
              <li>The dive computer UI actively discourages exceeding the 40 m rated depth limit: warnings are shown and haptics fire when approaching and when beyond it</li>
              <li>Never let any single instrument — including ZDIVE — be your only source of decompression information</li>
            </ul>
          </div>

          <div className="legal-subsection safety-warning">
            <h3>Apple Sensor Limitations</h3>
            <ul>
              <li><strong className="critical-warning">Live depth data on Apple Watch is currently limited to approximately 6 meters (20 feet) by Apple's public sensor API</strong></li>
              <li>Apple provides two depth sensor APIs: a public API (up to 6m) and a restricted API (up to 40m) available only to approved applications</li>
              <li>Full-depth support (up to 40 meters / 130 feet) is pending Apple's approval of our entitlement application; deeper dives can meanwhile be imported from Apple's Depth app via HealthKit</li>
              <li>Until then, do not rely on ZDIVE's live depth readings beyond 6 meters</li>
              <li>These limitations come from Apple's API access restrictions, not the Apple Watch hardware</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Safety Updates</h3>
            <ul>
              <li>We maintain infrastructure to notify you of safety-critical updates: the app checks a remote safety configuration, can alert you by notification, and will refuse to start new dives on a version with a known safety defect</li>
              <li>Keep automatic App Store updates enabled so safety fixes reach you promptly</li>
              <li>You can report bugs or safety concerns at any time from Settings → Contact Support (dive@zyork.dev)</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Diving Safety</h3>
            <ul>
              <li>ZDIVE is a tool to assist with dive logging and planning but does not replace proper training, certification, or professional guidance</li>
              <li>You must be properly certified and trained before engaging in diving activities</li>
              <li>Always follow established diving safety protocols and procedures</li>
              <li>Consult with qualified diving professionals for safety-critical decisions</li>
              <li>Use certified diving equipment and computers for all actual diving activities</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Medical Disclaimer</h3>
            <ul>
              <li>ZDIVE is not a medical device and should not be used for medical diagnosis or treatment</li>
              <li>Health data provided by the App is for informational purposes only</li>
              <li>Consult with healthcare professionals for medical advice related to diving</li>
              <li>Do not rely solely on App data for medical decisions</li>
            </ul>
          </div>

          <div className="legal-subsection safety-warning">
            <h3>Risk Acknowledgment</h3>
            <ul>
              <li>Diving involves inherent risks that can result in serious injury or death</li>
              <li>You assume all risks associated with diving activities</li>
              <li>We are not responsible for any injuries, damages, or losses resulting from diving activities</li>
              <li>Use the App at your own risk</li>
              <li><strong className="critical-warning">Relying on ZDIVE as your only dive instrument could result in serious injury or death — always dive with a certified primary dive computer</strong></li>
              <li><strong className="critical-warning">Always use certified diving equipment for all diving activities</strong></li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Acceptable Use</h2>

          <div className="legal-subsection">
            <h3>Permitted Uses</h3>
            <p>You may use ZDIVE for:</p>
            <ul>
              <li>Personal diving activities and training</li>
              <li>Recording and managing your dive logs</li>
              <li>Planning and organizing diving trips</li>
              <li>Educational purposes related to diving safety</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Prohibited Uses</h3>
            <p>You may not use ZDIVE to:</p>
            <ul>
              <li><strong className="critical-warning">Use as your sole source of decompression or depth information while diving</strong></li>
              <li><strong className="critical-warning">Rely on live depth readings beyond 6 meters for safety-critical decisions (current Apple API limitation)</strong></li>
              <li>Violate any applicable laws or regulations</li>
              <li>Harm, threaten, or harass others</li>
              <li>Distribute malicious software or harmful content</li>
              <li>Attempt to reverse engineer or modify the App</li>
              <li>Use the App for commercial purposes without permission</li>
              <li>Share false or misleading diving information</li>
              <li>Violate any third-party rights</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Data and Privacy</h2>

          <div className="legal-subsection">
            <h3>Data Collection</h3>
            <ul>
              <li>We collect data as described in our Privacy Policy</li>
              <li>Your data is used to provide and improve the Service</li>
              <li>We implement appropriate security measures to protect your data</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Data Ownership</h3>
            <ul>
              <li>You own your personal dive data</li>
              <li>You can export or delete your data at any time</li>
              <li>We do not claim ownership of your personal content</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Service Availability</h2>

          <div className="legal-subsection">
            <h3>Uptime</h3>
            <ul>
              <li>We strive to maintain high service availability but cannot guarantee uninterrupted access</li>
              <li>The Service may be temporarily unavailable for maintenance or updates</li>
              <li>We are not liable for any downtime or service interruptions</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Termination</h2>

          <div className="legal-subsection">
            <h3>Your Right to Terminate</h3>
            <ul>
              <li>You may stop using the Service at any time</li>
              <li>You can delete the App from your device</li>
              <li>You can request deletion of your data by contacting us</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Our Right to Terminate</h3>
            <ul>
              <li>We may suspend or terminate your access for violations of these Terms</li>
              <li>We may discontinue the Service with reasonable notice</li>
              <li>We may terminate accounts that are inactive for extended periods</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Disclaimers and Limitations</h2>

          <div className="legal-subsection">
            <h3>Service Disclaimers</h3>
            <ul>
              <li>The Service is provided "as is" without warranties of any kind</li>
              <li>We disclaim all warranties, express or implied, including merchantability and fitness for a particular purpose</li>
              <li>We do not warrant that the Service will be error-free or uninterrupted</li>
            </ul>
          </div>

          <div className="legal-subsection">
            <h3>Limitation of Liability</h3>
            <ul>
              <li>To the maximum extent permitted by law, we shall not be liable for any indirect, incidental, special, or consequential damages</li>
              <li>Our total liability shall not exceed the amount you paid for the Service (if any)</li>
              <li>We are not liable for any damages resulting from diving activities</li>
            </ul>
          </div>
        </section>

        <section className="legal-section">
          <h2>Contact Information</h2>
          <p>If you have any questions about these Terms of Service, please contact us at:</p>
          <div className="contact-info">
            <p><strong>Email:</strong> dive@zyork.dev</p>
            <p><strong>Website:</strong> https://zdive.app/</p>
          </div>
        </section>

        <section className="legal-section">
          <h2>Acknowledgment</h2>
          <p>
            <strong>By using ZDIVE, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.</strong>
          </p>
        </section>

        <div className="legal-footer">
          <p>
            These Terms of Service are effective as of the date listed above and will remain in effect until modified or terminated in accordance with these Terms.
          </p>
        </div>
      </div>
    </Container>
  );
}

export default TermsOfService;